export const DIRECTIONS = ["N", "E", "S", "W"] as const;
export type Direction = (typeof DIRECTIONS)[number];
export type Rotation = 0 | 180;

export const TOOLS = ["PICKAXE", "LANTERN", "CART"] as const;
export type Tool = (typeof TOOLS)[number];

export type PlayCardKind = "PATH" | "BREAK_TOOL" | "REPAIR_TOOL" | "MAP" | "ROCKFALL";
export type CardKind = PlayCardKind | "GOLD";

interface CatalogEntry {
  readonly faceId: string;
  readonly count: number;
  readonly label: string;
  readonly description: string;
}

export interface PathCardDefinition extends CatalogEntry {
  readonly kind: "PATH";
  /** Sides on which the printed tunnel reaches the edge of the card. */
  readonly openings: readonly Direction[];
  /** Internally connected groups. Singleton groups are printed dead ends. */
  readonly networks: readonly (readonly Direction[])[];
  readonly deadEnd: boolean;
}

export interface BreakToolCardDefinition extends CatalogEntry {
  readonly kind: "BREAK_TOOL";
  readonly tools: readonly [Tool];
}

export interface RepairToolCardDefinition extends CatalogEntry {
  readonly kind: "REPAIR_TOOL";
  readonly tools: readonly Tool[];
}

export interface MapCardDefinition extends CatalogEntry {
  readonly kind: "MAP";
}

export interface RockfallCardDefinition extends CatalogEntry {
  readonly kind: "ROCKFALL";
}

export type PlayCardDefinition =
  | PathCardDefinition
  | BreakToolCardDefinition
  | RepairToolCardDefinition
  | MapCardDefinition
  | RockfallCardDefinition;

const path = (
  faceId: string,
  count: number,
  openings: readonly Direction[],
  networks: readonly (readonly Direction[])[],
  deadEnd = false,
): PathCardDefinition => ({
  faceId,
  count,
  kind: "PATH",
  label: faceId
    .split("-")
    .map((word) => `${word[0]?.toUpperCase() ?? ""}${word.slice(1)}`)
    .join(" "),
  description: deadEnd ? "Tunnel branches ending inside the card." : "Connected tunnel card.",
  openings,
  networks,
  deadEnd,
});

/** The 40 ordinary path cards in the published base-game deck. */
export const PATH_CARD_CATALOG = [
  path("path-short-straight", 3, ["N", "S"], [["N", "S"]]),
  path("path-long-straight", 4, ["E", "W"], [["E", "W"]]),
  path("path-left-turn", 5, ["N", "W"], [["N", "W"]]),
  path("path-right-turn", 4, ["N", "E"], [["N", "E"]]),
  path("path-short-t", 5, ["N", "E", "W"], [["N", "E", "W"]]),
  path("path-long-t", 5, ["N", "E", "S"], [["N", "E", "S"]]),
  path("path-crossroads", 5, DIRECTIONS, [DIRECTIONS]),
  path("path-dead-crossroads", 1, DIRECTIONS, [["N"], ["E"], ["S"], ["W"]], true),
  path("path-short-dead-one", 1, ["N"], [["N"]], true),
  path("path-long-dead-one", 1, ["E"], [["E"]], true),
  path("path-short-dead-two", 1, ["N", "S"], [["N"], ["S"]], true),
  path("path-long-dead-two", 1, ["E", "W"], [["E"], ["W"]], true),
  path("path-left-dead", 1, ["N", "W"], [["N"], ["W"]], true),
  path("path-right-dead", 1, ["N", "E"], [["N"], ["E"]], true),
  path("path-short-dead-t", 1, ["N", "E", "W"], [["N"], ["E"], ["W"]], true),
  path("path-long-dead-t", 1, ["N", "E", "S"], [["N"], ["E"], ["S"]], true),
] as const satisfies readonly PathCardDefinition[];

/** Start and goal cards complete the official set of 44 path components. */
export const SPECIAL_PATH_CARD_CATALOG = [
  path("start", 1, DIRECTIONS, [DIRECTIONS]),
  path("goal-gold", 1, DIRECTIONS, [DIRECTIONS]),
  path("goal-rock", 2, DIRECTIONS, [DIRECTIONS]),
] as const satisfies readonly PathCardDefinition[];

const breakTool = (tool: Tool): BreakToolCardDefinition => ({
  faceId: `break-${tool.toLowerCase()}`,
  count: 3,
  kind: "BREAK_TOOL",
  label: `Break ${tool.toLowerCase()}`,
  description: `Breaks a player's ${tool.toLowerCase()}.`,
  tools: [tool],
});

const repairTool = (...tools: Tool[]): RepairToolCardDefinition => ({
  faceId: `repair-${tools.map((tool) => tool.toLowerCase()).join("-")}`,
  count: tools.length === 1 ? 2 : 1,
  kind: "REPAIR_TOOL",
  label: `Repair ${tools.map((tool) => tool.toLowerCase()).join(" or ")}`,
  description: `Repairs one matching broken ${tools.length === 1 ? "tool" : "tool shown"}.`,
  tools,
});

/** The 27 action cards in the published base-game deck. */
export const ACTION_CARD_CATALOG = [
  breakTool("PICKAXE"),
  breakTool("LANTERN"),
  breakTool("CART"),
  repairTool("PICKAXE"),
  repairTool("LANTERN"),
  repairTool("CART"),
  repairTool("PICKAXE", "LANTERN"),
  repairTool("PICKAXE", "CART"),
  repairTool("LANTERN", "CART"),
  {
    faceId: "map",
    count: 6,
    kind: "MAP",
    label: "Map",
    description: "Privately inspect one unrevealed goal.",
  },
  {
    faceId: "rockfall",
    count: 3,
    kind: "ROCKFALL",
    label: "Rockfall",
    description: "Remove one ordinary path card from the mine.",
  },
] as const satisfies readonly PlayCardDefinition[];

export const PLAY_CARD_CATALOG = [...PATH_CARD_CATALOG, ...ACTION_CARD_CATALOG] as const;
/** Ordinary path cards shuffled into the playing deck (excludes start and three goals). */
export const ORDINARY_PATH_CARD_COUNT = PATH_CARD_CATALOG.reduce(
  (sum, card) => sum + card.count,
  0,
);
/** Official component count: 40 ordinary paths + one start + three goals. */
export const PATH_CARD_COUNT = ORDINARY_PATH_CARD_COUNT + 4;
export const ACTION_CARD_COUNT = ACTION_CARD_CATALOG.reduce((sum, card) => sum + card.count, 0);
export const BASE_PLAY_DECK_COUNT = ORDINARY_PATH_CARD_COUNT + ACTION_CARD_COUNT;

export interface CardInstance {
  readonly cardId: string;
  readonly faceId: string;
  readonly kind: PlayCardKind;
}

export interface GoldCardInstance {
  readonly cardId: string;
  readonly faceId: `gold-${1 | 2 | 3}`;
  readonly kind: "GOLD";
  readonly nuggets: 1 | 2 | 3;
}

export const GOLD_CARD_CATALOG = [
  { faceId: "gold-1", kind: "GOLD", nuggets: 1, count: 16 },
  { faceId: "gold-2", kind: "GOLD", nuggets: 2, count: 8 },
  { faceId: "gold-3", kind: "GOLD", nuggets: 3, count: 4 },
] as const;

export const ROLE_CARD_CATALOG = [
  { role: "MINER", count: 7 },
  { role: "SABOTEUR", count: 4 },
] as const;

const definitionsByFaceId = new Map(
  PLAY_CARD_CATALOG.map((definition) => [definition.faceId, definition] as const),
);

export function getCardDefinition(faceId: string): PlayCardDefinition {
  const definition = definitionsByFaceId.get(faceId);
  if (!definition) throw new Error(`Unknown playing-card face: ${faceId}`);
  return definition;
}

const allPathDefinitionsByFaceId = new Map(
  [...PATH_CARD_CATALOG, ...SPECIAL_PATH_CARD_CATALOG].map((definition) => [
    definition.faceId,
    definition,
  ]),
);

export function getPathCardDefinition(faceId: string): PathCardDefinition {
  const definition = allPathDefinitionsByFaceId.get(faceId);
  if (!definition) throw new Error(`Unknown path-card face: ${faceId}`);
  return definition;
}

export const START_CARD_DEFINITION = getPathCardDefinition("start");
